import React from "react";
import {
  Activity,
  LineChart,
  Radar,
  ShieldAlert,
  Sparkles,
  Layers,
} from "lucide-react";

const pillars = [
  {
    icon: LineChart,
    title: "Dropping Odds Analysis",
    text:
      "Ryan continuously monitors pre-match line movement across the world's most liquid regulated bookmakers. Sharp money leaves a signature — sudden compression on one side often precedes a fundamental change (a late injury, weather shift, lineup leak). He isolates these signals from noise and acts only on the moves that carry statistical weight.",
    tag: "Signal detection",
  },
  {
    icon: Radar,
    title: "Multi-Factor Match Modelling",
    text:
      "Every fixture that gets published is stress-tested against a model that blends xG output, home/away splits, tactical matchups, referee tendencies, travel fatigue, and squad rotation. When the model's edge disagrees with the market price by more than the threshold, the fixture becomes a candidate — otherwise it is discarded.",
    tag: "Data-driven",
  },
  {
    icon: Layers,
    title: "Selective Publication",
    text:
      "Volume is the enemy of a serious punter. Rather than covering every kick-off on the calendar, only 1–2 picks are published per day — the ones where research, market read, and risk profile align. Fewer picks, higher conviction, cleaner results.",
    tag: "Quality over quantity",
  },
  {
    icon: ShieldAlert,
    title: "Bankroll & Risk Discipline",
    text:
      "Even the sharpest edge is worthless without disciplined staking. Every pick is tagged with a confidence level so members can size positions accordingly, and losses are never chased. Slow, steady, and repeatable beats hot streaks over a full season.",
    tag: "Long-term thinking",
  },
];

const stats = [
  { label: "Leagues actively monitored", value: "Almost all" },
  { label: "Fixtures screened per week", value: "1,200" },
  { label: "Picks published per day", value: "1–2" },
  { label: "Data points per fixture", value: "70+" },
];

const Methodology = () => {
  return (
    <section className="bg-ep-royal py-16 lg:py-24 relative overflow-hidden">
      <div className="absolute top-1/2 -translate-y-1/2 -left-40 w-[520px] h-[520px] rounded-full bg-[#1c3aa8] opacity-20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-24 w-[520px] h-[520px] rounded-full bg-[#ff5a5a] opacity-8 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase flex items-center gap-2">
            <Sparkles size={14} /> The Explorer Picks Method
          </div>
          <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
            Football betting, treated like a{" "}
            <span className="text-[#ff5a5a]">professional discipline</span>.
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed">
            Long-term profit at football markets is not luck — it is the
            compounded outcome of a repeatable process. The pillars below are
            the four rules that govern every selection Ryan publishes.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5 lg:gap-6">
          {pillars.map((p, i) => (
            <div
              key={i}
              className="card-lift group bg-white rounded-2xl p-7 lg:p-8 relative overflow-hidden"
            >
              <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-[#ff5a5a]/8 group-hover:bg-[#ff5a5a]/12 transition-colors" />
              <div className="relative flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl bg-[#0a1a4a] text-white flex items-center justify-center flex-shrink-0">
                  <p.icon size={24} strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a5a]">
                    {p.tag}
                  </div>
                  <h3 className="mt-1 text-[#0a1a4a] text-xl font-bold">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[#0a1a4a]/75 text-sm leading-relaxed">
                    {p.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm px-6 py-8 lg:px-10 lg:py-10">
          <div className="flex items-center gap-2 mb-6">
            <Activity size={18} className="text-[#ff5a5a]" />
            <span className="text-white/80 text-sm font-semibold tracking-widest uppercase">
              Behind every pick
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-white font-extrabold text-3xl lg:text-4xl">
                  {s.value}
                </div>
                <div className="text-white/60 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 max-w-3xl mx-auto text-center">
          <div className="text-white/60 uppercase tracking-widest text-xs font-semibold">
            The principle Ryan operates by
          </div>
          <blockquote className="mt-4 text-white text-xl lg:text-2xl font-medium leading-relaxed">
            &ldquo;In football markets, the price tells a story. My job is not
            to predict the future — it is to read the price better than the
            crowd, and to bet only when the price is wrong.&rdquo;
          </blockquote>
          <div className="mt-4 text-[#ff5a5a] text-sm font-semibold tracking-wide">
            — Ryan Fletcher
          </div>
        </div>
      </div>
    </section>
  );
};

export default Methodology;
